import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { FaEnvelope, FaMobileAlt, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";
import { auth, googleProvider } from "../firebase";
import { signInWithPopup } from "firebase/auth";
import api from "../utils/base_url";
import { useAuth } from "../context/AuthContext";
import "../styles/Register.css";

/* ── All logic unchanged — only JSX structure/classNames updated ── */

const Register = () => {
  const [registerMethod, setRegisterMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const isValidPassword = (v) =>
    /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{6,}$/.test(v);
  const isValidPhone = (v) => /^[0-9]{10,15}$/.test(v);
  const clearError = () => setTimeout(() => setErrors(""), 3000);

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) { setErrors("Please enter a valid email"); clearError(); return; }
    if (!isValidPassword(password)) {
      setErrors("Password needs 1 uppercase, 1 number, 1 special character & min 6 chars");
      clearError(); return;
    }
    setLoading(true);
    try {
      const res = await api.post("register/email-step1/", { email, password });
      toast.success(res.data.message || "OTP sent to email");
      sessionStorage.setItem("session_id", res.data.session_id);
      sessionStorage.setItem("email", email);
      sessionStorage.setItem("password", password);
      navigate("/verification", { state: { type: "email" } });
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.error || data?.message ||
        (typeof data === "object" ? Object.values(data)?.[0]?.[0] || Object.values(data)?.[0] : null) ||
        "Email registration failed";
      setErrors(msg); clearError();
    } finally { setLoading(false); }
  };

  const handlePhoneRegister = async (e) => {
    e.preventDefault();
    if (!isValidPhone(phone)) return toast.error("Please enter a valid phone number");
    setLoading(true);
    try {
      const res = await api.post("phone-register-step1/", { phone });
      toast.success(res.data.message || "OTP sent to phone");
      sessionStorage.setItem("session_id", res.data.session_id);
      sessionStorage.setItem("phone", phone);
      navigate("/verification", { state: { type: "phone" } });
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.error || data?.message ||
        (typeof data === "object" ? Object.values(data)[0]?.[0] || Object.values(data)[0] : null) ||
        "Phone registration failed";
      setErrors(msg); clearError();
    } finally { setLoading(false); }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      const res = await api.post("google/", { token: idToken });
      sessionStorage.setItem("access", res.data.access);
      sessionStorage.setItem("refresh", res.data.refresh);
      login({ access: res.data.access, refresh: res.data.refresh, user: res.data.user });
      toast.success("Google login successful!");
      navigate("/");
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message ||
        error.message || "Google login failed";
      setErrors(msg); clearError();
    } finally { setLoading(false); }
  };

  return (
    <div className="register-container">
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        className="register-card"
      >
        {/* Logo */}
        <img
          src="/mainlogo.png"
          alt="Nursery Logo"
          className="navbar-logo"
        />

        {/* Title */}
        <h1 className="register-title">Create account</h1>
        <p className="register-subtitle">Join our green community</p>

        {/* Method toggle */}
        <div className="register-method-toggle">
          <motion.button
            type="button"
            className={registerMethod === "email" ? "active" : ""}
            onClick={() => setRegisterMethod("email")}
            whileTap={{ scale: 0.95 }}
            aria-label="Register with email"
            title="Email registration"
          >
            <FaEnvelope />
          </motion.button>
          {/* Phone toggle — uncomment to enable */}
          {/* <motion.button
            type="button"
            className={registerMethod === "phone" ? "active" : ""}
            onClick={() => setRegisterMethod("phone")}
            whileTap={{ scale: 0.95 }}
            aria-label="Register with phone"
          >
            <FaMobileAlt />
          </motion.button> */}
        </div>

        {/* ── Email form ── */}
        {registerMethod === "email" && (
          <motion.form
            onSubmit={handleEmailRegister}
            className="register-form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="floating-input">
              <FaEnvelope className="field-icon" aria-hidden="true" />
              <input
                type="email"
                id="reg-email"
                placeholder=" "
                value={email}
                required
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
              />
              <label htmlFor="reg-email">Email address</label>
            </div>

            <div className="floating-input password-input">
              <FaLock className="field-icon" aria-hidden="true" />
              <input
                type={showPassword ? "text" : "password"}
                id="reg-password"
                placeholder=" "
                value={password}
                required
                autoComplete="new-password"
                onChange={(e) => setPassword(e.target.value)}
              />
              <label htmlFor="reg-password">Create password</label>
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {/* Subtle password hint */}
            <p className="password-hint">
              Use at least 6 characters with 1 uppercase, 1 number & 1 special character
            </p>

            {errors && <p className="auth-error">{errors}</p>}

            <button
              type="submit"
              className="btn btn-primary register-btn"
              disabled={loading}
            >
              {loading ? "Please wait…" : "Create account"}
            </button>
          </motion.form>
        )}

        {/* ── Phone form ── */}
        {registerMethod === "phone" && (
          <motion.form
            onSubmit={handlePhoneRegister}
            className="register-form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="floating-input">
              <FaMobileAlt className="field-icon" aria-hidden="true" />
              <input
                type="tel"
                id="reg-phone"
                placeholder=" "
                value={phone}
                required
                autoComplete="tel"
                onChange={(e) => setPhone(e.target.value)}
              />
              <label htmlFor="reg-phone">Phone number</label>
            </div>

            {errors && <p className="auth-error">{errors}</p>}

            <button
              type="submit"
              className="btn btn-primary register-btn"
              disabled={loading}
            >
              {loading ? "Sending…" : "Send OTP"}
            </button>
          </motion.form>
        )}

        {/* Divider */}
        <div className="auth-divider">
          <span>or continue with</span>
        </div>

        {/* Google */}
        <button
          type="button"
          onClick={handleGoogleAuth}
          className="btn-google-register"
          disabled={loading}
          aria-label="Register with Google"
        >
          <FcGoogle size={22} aria-hidden="true" />
          {loading ? "Please wait…" : "Continue with Google"}
        </button>

        {/* Login link */}
        <p className="login-prompt">
          Already have an account?{" "}
          <Link to="/login" className="text-themed">
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default Register;